import { useEffect, useCallback } from 'react';
import { initCampGA4, trackGA4Event } from '../utils/ga4Helper';

/**
 * Drop this hook into any page/component to activate GA4 tracking
 * for a specific camp when the page loads.
 *
 * @param {string} campCode      - campDetails.campCode from the API response
 * @param {object} programData - optional page-level data sent with the page_view
 *
 * Usage (page load — no user action):
 *   useGA4CampTracking(campDetails.campCode, {
 *     program_id: campDetails._id,
 *     program_name: campDetails.programName,
 *     camp_name: campDetails.campName,
 *     page_type: 'camp_detail',
 *   });
 *
 * For user actions (add to cart, booking, payment) use trackGA4Event instead:
 *   import { trackGA4Event } from '../utils/ga4Helper';
 *   trackGA4Event(campCode, 'add_to_cart', { program_id, price, quantity });
 *
 * Re-fires automatically if campCode changes (handles SPA navigation).
 * If campCode is not in CAMP_GA4_MAP it silently does nothing.
 */
/**
 * @param {string} campCode  - campDetails.campCode from the API response
 * @param {object} info    - optional page-level data sent with the page_view
 * @param {boolean} init   - set to false when you only need trackEvent and do NOT
 *                           want a page_view fired (e.g. event-only pages like OnlineStore)
 */
export function useGA4CampTracking(campCode, info = {}, init = true) {
  useEffect(() => {
    if (campCode && init) initCampGA4(campCode, info);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campCode]);

  // Returns a trackEvent bound to campCode so components never repeat campCode on every call.
  const trackEvent = useCallback((eventName, eventData = {}) => {
    trackGA4Event(campCode, eventName, eventData);
  }, [campCode]);

  return { trackEvent };
}
